import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import uuid from 'uuid-random';
import Alert from '@panel/commons/lib/Alert/Alert';
import SimpleDropDown from '@panel/commons/lib/DropDown/SimpleDropDown';
import { ModalBase, sendCustomTrackingInfo } from '@panel/commons';
import InputDataContainer from '../InputContactData';
import {
  ContactDataContainer,
  SectionContainer,
  AlertContainer,
  Answer,
  Title,
  LineBottom,
  SectionPreview
} from './styles';
import { Modal } from '../Modals/ModalWrapper';
import Preview from '../Preview';
import UpdateDataModal from '../Modals/UpdateDataModal';
import { ContactDataSelectors, ContactDataOperations } from '../../ducks/contact-data';
import { CollaboratorsSelectors, CollaboratorsOperations } from '../../ducks/collaborators';

const ContactData = ({
  device,
  userInfo,
  postingId,
  setFinishedAction = () => {},
  setActiveLoader = () => {},
  setDisabledSkeleton = () => {}
}) => {
  const { t } = useTranslation();
  const [updateDataModal, setUpdateDataModal] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [sortedCollaborators, setSortedCollaborators] = useState([]);
  const [userResponsible, setUserResponsible] = useState({
    email: null,
    isResponsible: true,
    lastName: null,
    name: t('panel.contactosPermisos.ContactData.isResponsible.notHasResponsible'),
    permission: true,
    phone: null,
    userId: uuid(),
    userTypeId: null
  });
  const [newUserNameResponsible, setNewUserNameResponsible] = useState(null);
  const dispatch = useDispatch();
  const {
    postingContactData,
    hasPostingContactData,
    errorGetPostingContactData,
    updatedUserResponsible,
    updatingUserResponsible,
    errorUpdatingUserResponsible
  } = useSelector(ContactDataSelectors.getContactDataSelectors);

  const { collaboratorsInfo, hasCollaboratorsInfo, errorCollaborators } = useSelector(
    CollaboratorsSelectors.getCollaboratorsState
  );

  /**
   * La función evalua los actions y results para armar las traducciones de los snackbars
   * @param {string} action
   * @param {string} result
   * @param {string} name
   * @returns Se retorna la traducción armada
   */
  const snackBarTextBuilder = (action, result, name) => {
    if (action === 'updateResponsible' && result === 'ERROR') {
      return t(`panel.contactosPermisos.${action}.snackbar.${result}`, { userName: name });
    }
    return t(`panel.contactosPermisos.${action}.snackbar.${result}`);
  };

  /**
   * Retorna el texto que se va a mostrar en las opciones del DropDown.
   * @param {Object} collaborator
   * @returns {String}
   */
  const getTextDropDown = collaborator => {
    if (!collaborator.email) return `${collaborator.name}`;
    if (!collaborator.lastName && !collaborator.phone) return `${collaborator.name} (${collaborator.email})`;
    if (!collaborator.lastName) return `${collaborator.name} (${collaborator.email} - ${collaborator.phone})`;
    if (collaborator.phone && collaborator.lastName)
      return `${collaborator.name} ${collaborator.lastName} (${collaborator.email} - ${collaborator.phone})`;
    return `${collaborator.name} ${collaborator.lastName} (${collaborator.email})`;
  };

  /**
   * La función ordena el listado de usuarios en primer lugar por Responsable y luego por tipo de usuario (Admin > Superusuario)
   * @param {Array} usersArray
   * @returns se retorna el array de usuarios ordenado
   */
  const sortCollaboratorsList = collaboratorsArray => {
    if (!collaboratorsArray) return [];
    collaboratorsArray.sort((a, b) => {
      if (a.isResponsible > b.isResponsible) return -1;
      if (a.isResponsible < b.isResponsible) return 1;
      if (a.permission > b.permission) return -1;
      if (b.permission > a.permission) return 1;
      return a.userTypeId - b.userTypeId;
    });
    return collaboratorsArray;
  };

  const onCloseUpdateModal = () => {
    sendCustomTrackingInfo({
      event: 'click_modal',
      modal_name: 'Modal actualización de datos de contacto',
      item_id: 'Cerrar'
    });
    setUpdateDataModal(false);
  };

  const onClickUpdateResponsible = collaborator => {
    dispatch(ContactDataOperations.updateUserResponsibleForPosting(postingId, collaborator.userId));
    setNewUserNameResponsible(collaborator.name);
  };

  const onClickDeletePostingPhone = () => {
    dispatch(ContactDataOperations.updateUserResponsibleForPosting(postingId, userResponsible.userId, true));
  };

  useEffect(() => {
    // Se realiza FETCH para armar el dropdown la primera vez con los usuarios y cada vez que se cambia el responsable del aviso (updatedUserResponsible):
    if ((!hasCollaboratorsInfo || updatedUserResponsible) && !errorCollaborators) {
      dispatch(CollaboratorsOperations.getCollaboratorsInfo(postingId));
    }
    // Se setea el usuario responsable del aviso:
    collaboratorsInfo.forEach(collaborator => {
      if (collaborator.isResponsible) return setUserResponsible(collaborator);
      return null;
    });
    // Se odenan el listado de usuarios, mostrando de primero los usuarios con permisos para editar datos:
    setSortedCollaborators(sortCollaboratorsList(collaboratorsInfo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collaboratorsInfo, updatedUserResponsible]);

  useEffect(() => {
    // Se realiza FETCH para traer la primera vez los datos de la Información de contacto-Vista previa y cada vez que se cambia el responsable del aviso (updatedUserResponsible):
    if ((!hasPostingContactData || updatedUserResponsible) && !errorGetPostingContactData) {
      dispatch(ContactDataOperations.getPostingContactData(postingId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingContactData, updatedUserResponsible]);

  // Efecto para mostrar el LOADER al cambiar permisos de los usuarios:
  useEffect(() => {
    if (updatingUserResponsible) {
      setActiveLoader(true);
      // Se realiza el siguiente set para evitar que se muestre el skeleton al cambiar los permisos:
      setDisabledSkeleton(true);
    } else {
      setActiveLoader(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatingUserResponsible, updatedUserResponsible]);

  // Efecto para disparar SNACKBARS al cambiar el nuevo responsable del aviso:
  useEffect(() => {
    if (updatedUserResponsible) {
      setFinishedAction({
        action: 'updateResponsible',
        result: 'SUCCESS',
        component: snackBarTextBuilder('updateResponsible', 'SUCCESS'),
        keepOpen: false
      });
    }
    if (errorUpdatingUserResponsible) {
      setFinishedAction({
        action: 'updateResponsible',
        result: 'ERROR',
        component: snackBarTextBuilder('updateResponsible', 'ERROR', newUserNameResponsible),
        keepOpen: false
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedUserResponsible, errorUpdatingUserResponsible]);

  return (
    <>
      {updateDataModal && (
        <Modal>
          <ModalBase
            openModal={updateDataModal}
            onClose={() => onCloseUpdateModal()}
            width={device !== 'mobile' ? '400px' : '92%'}
            height='396px'
            isLoading={false}
            withoutOutsideClose
          >
            <UpdateDataModal
              onClose={() => setUpdateDataModal(false)}
              userName={postingContactData && postingContactData.user_responsible_name}
              handleOnClick={onClickDeletePostingPhone}
            />
          </ModalBase>
        </Modal>
      )}
      {userInfo && userInfo.isIntegrator && (
        <AlertContainer>
          <Alert
            message={t('panel.contactosPermisos.ContactData.isIntegrator.message')}
            dataPabel='ContactData'
            minHeight='46px'
            fontSize='14px'
            justifyContent='start'
            withoutBorder
          />
        </AlertContainer>
      )}
      <ContactDataContainer device={device}>
        <SectionContainer device={device}>
          {hasCollaboratorsInfo && (
            <>
              <Title>{t('panel.contactosPermisos.ContactData.dropDown.title')}</Title>
              <SimpleDropDown
                dropdownOpen={dropdownOpen}
                setDropdownOpen={setDropdownOpen}
                textHeader={getTextDropDown(userResponsible)}
                tooltip={getTextDropDown(userResponsible)}
              >
                {sortedCollaborators &&
                  sortedCollaborators.length > 0 &&
                  sortedCollaborators.map(collaborator => (
                    <Answer
                      key={uuid()}
                      device='desktop'
                      data-panel='dropDownAnswer'
                      permission={collaborator.permission}
                      title={getTextDropDown(collaborator)}
                      onClick={() => {
                        if (!collaborator.isResponsible && collaborator.permission) {
                          onClickUpdateResponsible(collaborator);
                        }
                      }}
                    >
                      {getTextDropDown(collaborator)}
                    </Answer>
                  ))}
              </SimpleDropDown>
            </>
          )}
          <LineBottom />
          <InputDataContainer postingContactData={postingContactData} userInfo={userInfo} />
        </SectionContainer>
        <SectionPreview device={device}>
          <Preview
            device={device}
            userInfo={userInfo}
            postingData={postingContactData}
            setUpdateDataModal={setUpdateDataModal}
          />
        </SectionPreview>
      </ContactDataContainer>
    </>
  );
};

ContactData.propTypes = {
  device: PropTypes.string.isRequired,
  userInfo: PropTypes.shape({ isIntegrator: PropTypes.bool }).isRequired,
  postingId: PropTypes.string.isRequired
};

export default ContactData;
